/**
 * Typisierte Hooks für `/api/v1/energy/meters` (Spec 46a, 46b) — Zählerliste
 * mit Hausliste, Zählerdetail, Auswahllisten, Anlegen und Ändern (46a); Stand
 * erfassen samt Belegfoto, Stand löschen, Zählertausch, Stilllegen und
 * Wieder in Betrieb nehmen (46b); Lieferverträge anlegen, ändern, löschen (46c).
 *
 * `fetch` steht ausschließlich über `request()` hier, nie in einer Komponente.
 * Query-Keys kommen aus `lib/query-keys.ts`; jede Mutation invalidiert das
 * Präfix `keys.energy.all` (Liste, `houses` und Detail ändern sich zugleich).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'
import { uploadFileToStorage } from '@/lib/storage-upload'

export type MeterList = components['schemas']['MeterList']
export type MeterRow = components['schemas']['MeterRow']
export type MeterHouse = components['schemas']['MeterHouse']
export type MeterDetail = components['schemas']['MeterDetail']
export type MeterReading = components['schemas']['MeterReading']
export type MeterCreate = components['schemas']['MeterCreate']
export type MeterUpdate = components['schemas']['MeterUpdate']
export type MeterOptions = components['schemas']['MeterOptions']
export type MeterReadingCreate = components['schemas']['MeterReadingCreate']
export type MeterReplacementCreate = components['schemas']['MeterReplacementCreate']
export type SupplyContract = components['schemas']['SupplyContract']
export type SupplyContractCreate = components['schemas']['SupplyContractCreate']
export type SupplyContractUpdate = components['schemas']['SupplyContractUpdate']
export type CancellableContract = components['schemas']['CancellableContract']

/** Obergrenze der Seitengröße (`clamp_limit`). Ein Haushalt hat ein Dutzend
 * Zähler; die Liste lädt eine Seite und kennt keinen Cursor.
 * ponytail: ab mehr als 200 Zählern fehlt eine Folgeseite — dann `next_cursor`
 * mit `useInfiniteQuery` nachziehen. */
const METERS_LIMIT = 200

export function useMeters(house: string | undefined) {
  return useQuery({
    queryKey: keys.energy.meters({ house }),
    queryFn: ({ signal }) => {
      const query = new URLSearchParams({ limit: String(METERS_LIMIT) })
      if (house !== undefined) query.set('house', house)
      return request<MeterList>(`/energy/meters?${query.toString()}`, { signal })
    },
    // Beim Wechsel des Hauses bleiben Zeilen und Hauschips stehen, bis die
    // neue Antwort da ist — kein Skelett pro Filterwechsel (Spec 41).
    placeholderData: keepPreviousData,
  })
}

export function useMeter(meterId: string | undefined) {
  return useQuery({
    queryKey: keys.energy.meter(meterId ?? ''),
    queryFn: ({ signal }) => request<MeterDetail>(`/energy/meters/${meterId ?? ''}`, { signal }),
    enabled: meterId !== undefined,
  })
}

/** Art (mit Einheit) und Tarif des Formulars — der Client pflegt keine eigene
 * Zuordnung von Werten auf Bezeichnungen. */
export function useMeterOptions(enabled = true) {
  return useQuery({
    queryKey: keys.energy.meterOptions,
    queryFn: ({ signal }) => request<MeterOptions>('/energy/meter-options', { signal }),
    staleTime: Infinity,
    enabled,
  })
}

export function useCreateMeter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: MeterCreate) =>
      request<MeterDetail>('/energy/meters', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}

interface UpdateMeterArgs {
  readonly meterId: string
  readonly body: MeterUpdate
  readonly ifMatch: string
}

export function useUpdateMeter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ meterId, body, ifMatch }: UpdateMeterArgs) =>
      request<MeterDetail>(`/energy/meters/${meterId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}

interface CreateReadingArgs {
  readonly meterId: string
  readonly body: MeterReadingCreate
  /** Der Aufrufer hält ihn zwischen Versuchen mit gleichem Rumpf (Wiederholen
   * nach Netzfehler darf keinen zweiten Stand anlegen). */
  readonly idempotencyKey: string
}

/** `POST …/readings` — Stand erfassen, `editor`+ (Spec 46b, Regeln 2 und 3). */
export function useCreateReading() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ meterId, body, idempotencyKey }: CreateReadingArgs) =>
      request<MeterReading>(`/energy/meters/${meterId}/readings`, { method: 'POST', body, idempotencyKey }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}

interface DeleteReadingArgs {
  readonly meterId: string
  readonly readingId: string
}

export function useDeleteReading() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ meterId, readingId }: DeleteReadingArgs) =>
      request<undefined>(`/energy/meters/${meterId}/readings/${readingId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}

interface CreateReplacementArgs {
  readonly meterId: string
  readonly body: MeterReplacementCreate
}

/** `POST …/replacements` — Zählertausch (Spec 46b, Regel 6). Antwort ist das
 * Detail mit der neuen Nummer und um eins erhöhter `version`. */
export function useCreateReplacement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ meterId, body }: CreateReplacementArgs) =>
      request<MeterDetail>(`/energy/meters/${meterId}/replacements`, {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}

interface ArchiveMeterArgs {
  readonly meterId: string
  readonly ifMatch: string
}

/** `POST …/archive` und `…/unarchive` — Stilllegen, `manager`+ (Spec 46b,
 * Regel 10). `If-Match` ist Pflicht. */
function useMeterArchiving(action: 'archive' | 'unarchive') {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ meterId, ifMatch }: ArchiveMeterArgs) =>
      request<MeterDetail>(`/energy/meters/${meterId}/${action}`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}

export function useArchiveMeter() {
  return useMeterArchiving('archive')
}

export function useUnarchiveMeter() {
  return useMeterArchiving('unarchive')
}

interface CreateReadingPhotoArgs {
  readonly meterId: string
  readonly file: File
  /** Der Tag der Ablesung — wird `document_date` des Belegs. */
  readonly readOn: string
}

/** Titel des Belegdokuments (Spec 46b, Regel 3). */
const PHOTO_TITLE = 'Zählerstand'

/**
 * Das Belegfoto als Dokument am Zähler: `POST /documents/upload-url` → `PUT`
 * direkt in den Storage → `POST /documents` (Spec 46b, Regel 3; Original
 * byte-gleich). Liefert die `document.id`. Die beiden Dokumentaufrufe stehen
 * hier, weil `features/documents/api.ts` nicht importierbar ist; das Foto ist
 * ein Dokument, deshalb invalidiert die Mutation auch `documents.all`.
 */
export function useCreateReadingPhoto() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ meterId, file, readOn }: CreateReadingPhotoArgs): Promise<string> => {
      const signed = await request<{ upload_url: string; storage_key: string }>('/documents/upload-url', {
        method: 'POST',
        body: { subject_id: meterId, original_filename: file.name, mime_type: file.type, byte_size: file.size },
      })
      await uploadFileToStorage(signed.upload_url, file)
      const created = await request<{ id: string }>('/documents', {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
        body: {
          storage_key: signed.storage_key,
          original_filename: file.name,
          mime_type: file.type,
          byte_size: file.size,
          subject_id: meterId,
          title: PHOTO_TITLE,
          document_date: readOn,
          category: 'meter_reading',
          text_source: 'none',
        },
      })
      return created.id
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
      void queryClient.invalidateQueries({ queryKey: keys.documents.all })
    },
  })
}

/** `GET /documents/{id}/download-url` — der Betrachter des Belegfotos
 * (`viewer`+ am Zähler). Als Mutation: ein Klick, kein gemounteter Zustand. */
export function useMeterPhotoUrl() {
  return useMutation({
    mutationFn: (documentId: string) =>
      request<{ download_url: string }>(`/documents/${documentId}/download-url`),
  })
}

interface CreateSupplyContractArgs {
  readonly meterId: string
  readonly body: SupplyContractCreate
}

/** `POST …/supply-contracts` — Liefervertrag anlegen, `editor`+ intern
 * (Spec 46c, Regel 1). Detail, Liste und der Block „Demnächst kündbar"
 * ändern sich zugleich: Präfix `energy.all`. */
export function useCreateSupplyContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ meterId, body }: CreateSupplyContractArgs) =>
      request<SupplyContract>(`/energy/meters/${meterId}/supply-contracts`, {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}

interface UpdateSupplyContractArgs {
  readonly contractId: string
  readonly body: SupplyContractUpdate
  readonly ifMatch: string
}

/** `PATCH /energy/supply-contracts/{id}` — `If-Match` ist Pflicht (Regel 15). */
export function useUpdateSupplyContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ contractId, body, ifMatch }: UpdateSupplyContractArgs) =>
      request<SupplyContract>(`/energy/supply-contracts/${contractId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}

/** `DELETE /energy/supply-contracts/{id}` — endgültig, 204 (Regel 17). */
export function useDeleteSupplyContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (contractId: string) =>
      request<undefined>(`/energy/supply-contracts/${contractId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.energy.all })
    },
  })
}
